import React, { useEffect, useState } from "react";
import { Button, Col, Form, Input, Layout, Modal, Popconfirm, Row, Select, Table, Tag, Typography, message } from "antd";
import {
    getBusOperatorsByCreatedBy,
    createBusOperator,
    updateBusOperator,
    findSimilarBusOperators
} from "../../actions/bus-operator.action";
import { isValid } from "../../utils/utilities";
import useColumnSearch from "../../ui-components/table-column-search";

const { Title } = Typography;
const { Content, Footer } = Layout;
const { Option } = Select;

const REGIONS = ["south", "north"];

export const BusOperatorPage = () => {

    const [form] = Form.useForm();
    const [busOperators, setBusOperators] = useState([]);
    const [loading, setLoading] = useState(false);
    const [addLoading, setAddLoading] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState({});
    const [markActiveCode, setMarkActiveCode] = useState(null);

    const isEditing = isValid(selectedRow) && isValid(selectedRow.id);

    const fetchBusOperators = () => {
        setLoading(true);
        getBusOperatorsByCreatedBy(
            (data) => {
                setBusOperators(Array.isArray(data) ? data : []);
                setLoading(false);
            },
            (errMsg) => {
                setLoading(false);
                message.error(errMsg);
            }
        );
    };

    useEffect(() => {
        fetchBusOperators();
    }, []);

    useEffect(() => {
        if (isEditing) {
            form.setFieldsValue({
                name: selectedRow.name,
                operatorCode: selectedRow.operator_code,
                websiteUrl: selectedRow.website_url,
                region: selectedRow.region
            });
        } else {
            form.resetFields();
        }
    }, [selectedRow]);

    const onSuccess = (msg) => {
        if (isValid(msg)) {
            message.success(msg);
        }
        setAddLoading(false);
        setUpdateLoading(false);
        setSelectedRow({});
        form.resetFields();
        fetchBusOperators();
    };

    const onFailure = (errMsg) => {
        setAddLoading(false);
        setUpdateLoading(false);
        message.error(errMsg);
    };

    const confirmSimilarOperators = (similarOperators, onProceed) => {
        Modal.confirm({
            title: "Similar operator exists. Do you want to change?",
            content: (
                <div>
                    {similarOperators.map((operator) => (
                        <div key={operator.id}>{operator.name} ({operator.operator_code})</div>
                    ))}
                </div>
            ),
            okText: "Proceed",
            cancelText: "Edit",
            onOk: onProceed,
            onCancel: () => setAddLoading(false)
        });
    };

    const handleSubmit = (buttonType) => {
        form.validateFields()
            .then((values) => {
                const { name, operatorCode, websiteUrl, region } = values;
                if (buttonType === "add") {
                    setAddLoading(true);
                    findSimilarBusOperators({ operatorCode, name },
                        (similarOperators) => {
                            if (similarOperators.length > 0) {
                                confirmSimilarOperators(similarOperators, () => {
                                    createBusOperator({ name, operatorCode, websiteUrl, region }, onSuccess, onFailure);
                                });
                            } else {
                                createBusOperator({ name, operatorCode, websiteUrl, region }, onSuccess, onFailure);
                            }
                        },
                        onFailure
                    );
                } else {
                    // Only name and website url can be updated
                    setUpdateLoading(true);
                    updateBusOperator({
                        operatorCode: selectedRow.operator_code,
                        name,
                        websiteUrl
                    }, onSuccess, onFailure);
                }
            })
            .catch((errorInfo) => {
                console.log({ errorInfo });
            });
    };

    const handleMarkActive = (record) => {
        setMarkActiveCode(record.operator_code);
        updateBusOperator({
            operatorCode: record.operator_code,
            name: record.name,
            websiteUrl: record.website_url,
            isActive: 1
        },
        (msg) => {
            setMarkActiveCode(null);
            onSuccess(msg);
        },
        (errMsg) => {
            setMarkActiveCode(null);
            onFailure(errMsg);
        });
    };

    const columns = [
        {
            title: "Operator Name",
            dataIndex: "name",
            defaultSortOrder: "ascend",
            sorter: (a, b) => (isValid(a.name) ? a.name : "").localeCompare(isValid(b.name) ? b.name : ""),
            ...useColumnSearch({ dataIndexExt: "name" })
        },
        {
            title: "Code",
            dataIndex: "operator_code",
            ...useColumnSearch({ dataIndexExt: "operator_code" })
        },
        {
            title: "Website URL",
            dataIndex: "website_url"
        },
        {
            title: "Region",
            dataIndex: "region"
        },
        {
            title: "Created By",
            dataIndex: "created_by"
        },
        {
            title: "Status",
            dataIndex: "is_active",
            render: (isActive, record) => {
                if (isActive) {
                    return <Tag color="green">Active</Tag>;
                }
                return (
                    <Row gutter={[8, 8]} align="middle">
                        <Col>
                            <Tag color="red">Inactive</Tag>
                        </Col>
                        <Col>
                            <Popconfirm
                                title={`Mark ${record.name} as active?`}
                                okText="Yes"
                                cancelText="No"
                                onConfirm={(e) => {
                                    e.stopPropagation();
                                    handleMarkActive(record);
                                }}
                                onCancel={(e) => e.stopPropagation()}
                            >
                                <Button
                                    size="small"
                                    loading={markActiveCode === record.operator_code}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Mark Active
                                </Button>
                            </Popconfirm>
                        </Col>
                    </Row>
                );
            }
        }
    ];

    return (
        <div className="bus-operator-page" style={{ margin: "20px" }}>
            <Title level={4}>Bus Operators</Title>
            <Table
                dataSource={busOperators}
                columns={columns}
                loading={loading}
                pagination={{ pageSize: 10 }}
                rowKey={(record) => record.id}
                rowClassName={(record) => record.id === selectedRow.id ? "TableRowClicked" : ""}
                onRow={(record) => ({
                    onClick: () => {
                        setSelectedRow(record);
                    }
                })}
            />
            <Layout style={{ marginTop: "20px", background: "transparent" }}>
                <Title level={4}>Bus Operator Form</Title>
                <Content>
                    <Form layout="inline" form={form} name="bus-operator-form" initialValues={{}}>
                        <Form.Item
                            label="Operator name"
                            name="name"
                            rules={[{ required: true, message: "Please input operator name!" }]}
                        >
                            <Input placeholder="Operator name" />
                        </Form.Item>
                        <Form.Item
                            label="Code"
                            name="operatorCode"
                            normalize={(value) => isValid(value) ? value.toUpperCase() : value}
                            rules={[
                                { required: true, message: "Please input operator code!" },
                                { min: 3, message: "Operator code must be at least 3 characters!" }
                            ]}
                        >
                            <Input placeholder="Operator code" disabled={isEditing} />
                        </Form.Item>
                        <Form.Item
                            label="Website URL"
                            name="websiteUrl"
                            rules={[{ required: true, message: "Please input website url!" }]}
                        >
                            <Input placeholder="Website URL" />
                        </Form.Item>
                        <Form.Item
                            label="Region"
                            name="region"
                            rules={[{ required: true, message: "Please select region!" }]}
                        >
                            <Select placeholder="Select region" style={{ minWidth: 120 }} disabled={isEditing}>
                                {REGIONS.map((region) => (
                                    <Option key={region} value={region}>{region}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Form>
                </Content>
                <Footer style={{ background: "transparent", paddingLeft: 0 }}>
                    <Row gutter={[8, 8]}>
                        <Col>
                            <Button
                                loading={addLoading}
                                style={{ background: "#2EC17C", borderColor: "#2EC17C", color: "#fff" }}
                                disabled={isEditing}
                                onClick={() => handleSubmit("add")}
                            >
                                Add
                            </Button>
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                loading={updateLoading}
                                disabled={!isEditing}
                                onClick={() => handleSubmit("update")}
                            >
                                Update
                            </Button>
                        </Col>
                        <Col>
                            <Button onClick={() => setSelectedRow({})}>
                                Clear
                            </Button>
                        </Col>
                    </Row>
                </Footer>
            </Layout>
        </div>
    );
};
